import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '../config/supabase';
import jwtService from '../services/jwtService';
import { setCurrentUser } from '../utils/auth';

const QRLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    
    if (!token) {
      setError('Invalid QR code. No token found.');
      setLoading(false);
      return;
    }

    // Verify token and login user
    const verifyAndLogin = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/user-auth/qr-login/verify`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token }),
        });

        const result = await response.json();

        if (result.success && result.user) {
          setMessage('Login successful! Redirecting...');
          
          // Store user data
          setCurrentUser(result.user);
          
          // Store JWT token
          jwtService.storeUserToken({
            id: result.user.id,
            unique_id: result.user.unique_id,
            mobile_number: result.user.mobile_number,
            email_id: result.user.email_id,
            user_name: result.user.user_name,
            token: result.token,
            expiresAt: result.expiresAt,
            expiresIn: result.expiresIn
          });

          // Redirect to verification page after short delay
          setTimeout(() => {
            navigate('/verification');
          }, 1000);
        } else {
          setError(result.error || 'Login failed. Please try again.');
          setLoading(false);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to verify QR login. Please try again.');
        setLoading(false);
      }
    };

    verifyAndLogin();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-black/20 backdrop-blur-sm flex items-center justify-center" style={{backgroundImage: 'url(/images/Asset5.png)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', backgroundAttachment: 'fixed'}}>
      <div className="w-full max-w-sm sm:max-w-md mx-auto px-4 py-4 sm:py-6 md:py-8">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          <div className="bg-gradient-to-r from-orange-400 to-orange-500 p-4 sm:p-6 text-center">
            <div className="flex items-center justify-center mb-3">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white rounded-full border-2 border-white flex items-center justify-center">
                <img 
                  src="/images/Asset2.png" 
                  alt="Temple Logo" 
                  className="w-12 h-12 sm:w-16 sm:h-16 object-contain"
                />
              </div>
            </div>
            <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-white">QR Code Login</h1>
          </div>

          <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Verifying QR code and logging you in...</p>
              </div>
            ) : error ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center">
                  <span className="text-red-600 text-lg mr-2">⚠️</span>
                  <p className="text-red-800 text-sm font-medium">{error}</p>
                </div>
                <button
                  onClick={() => navigate('/')}
                  className="mt-4 w-full bg-gray-600 hover:bg-gray-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
                >
                  Go to Home
                </button>
              </div>
            ) : message ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center">
                  <span className="text-green-600 text-lg mr-2">✅</span>
                  <p className="text-green-800 text-sm font-medium">{message}</p>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRLoginPage;

